import React, { useState, useEffect } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { Alert, Box, Card, CardContent, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, IconButton, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import { EEScorecardService } from "../../Component/Api/EmploymentEquity/Scorecard/EEScorecardService";
import { IDropDownOptions } from "../../Component/Common/Components/FormValidationInputs/AppSelectList";
import { CoreStaticDataService } from "../../Component/Api/StaticData/CoreStaticDataService";
import { getIOptionsArray } from "../../Helpers/arrayHelper";
import { IntegrationProcessService } from "../../Component/Api/IntegrationProcess/IntegrationProcessService";
import { Submissions } from "../../Component/Model/Dto/VerificationAgency/IntegrationProcess/Submissions";
import { LinearProgressWithLabel } from "../EmploymentEquity/GeneralInformationPage/GeneralInformation";
import { VASubmissionProgressClient } from "../../Services/SignalR/VASubmissionProgressClient";
import CloseIcon from '@mui/icons-material/Close';
import colors from "../../assets/theme/base/colors";
import theme from "../../assets/theme";

const { success, secondary, primary } = colors;

interface IProps {
    verificationAgencyName: string;
    scorecardId: string;
    scorecardName: string;
    showSubmissionModal: boolean;
    closeSubmissionModal: (resetScorecard?: boolean) => void;
}

export const SubmitScorecardModal = (props: IProps) => {
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const { handleSubmit } = useForm({ mode: 'all', });
    const [allowedElements, setAllowedElements] = React.useState<IDropDownOptions[]>([]);
    const [progress, setProgress] = React.useState(0);
    const [counter, setCounter] = React.useState<number>(0);
    var vaSubmissionProgressClient = new VASubmissionProgressClient();
    const [showErrorAndMessage, setShowErrorAndMessage] = React.useState({
        message: '',
        isError: false
    });
    const [scorecardCheckbox, setScorecardCheckbox] = useState(false);
    const [verificationAgencyCheckbox, setVerificationAgencyCheckbox] = useState(false);

    let intervalId: any;

    useEffect(() => {
        fetchData();
        vaSubmissionProgressClient.joinGroup(
            `VASubmission-${props.scorecardId}`,
            UpdateSubmissionStatus
        );
        return () => {
            vaSubmissionProgressClient.removeListeners(UpdateSubmissionStatus);
        };
    }, [props.scorecardId]);


    const UpdateSubmissionStatus = (message: any, percentage?: any): void => {
        if (message.includes("failed")) {
            setShowErrorAndMessage({
                message: message,
                isError: true
            });
            setIsSaving(false);
            setProgress(0);
        } else if (percentage) {
            setProgress(percentage);
        }
    };

    const removeKey = () => {
        if (props.scorecardId) {
            IntegrationProcessService.RemoveRedisKeyCheck(props.scorecardId);
        }

    }

    const fetchData = async () => {
        try {
            if (props.scorecardId) {
                let meta = await EEScorecardService.GetScorecardMeta(props.scorecardId);
                if (meta.data) {
                    const elementsPromise = await CoreStaticDataService.getScorecardAllowedElements(meta.data.scorecardType);
                    const [elements] = await Promise.all([
                        elementsPromise
                    ]);
                    const elementsData = getIOptionsArray(elements.data, "id", "name");
                    setAllowedElements(elementsData);
                }
            }
        } catch (error) {
            console.log(error);
        }
    }

    const signalRcheck = () => {
        const signalRIntervalId = setInterval(uploadStuckCheck, 10000);
        intervalId = signalRIntervalId;
    }

    const uploadStuckCheck = async () => {
        if (props.scorecardId) {
            const result = await IntegrationProcessService.CopyProgressStuckCheck(props.scorecardId);
            if (result.data.Status.includes("Completed")) {
                clearInterval(intervalId);
                removeKey();
                setIsSaving(false);
                toast.success("Scorecard submitted successfully");
                setCounter(counter + 1);
                setProgress(0);
                setShowErrorAndMessage({
                    message: '',
                    isError: false
                });
                props.closeSubmissionModal(true);
            } else if (result.data.Status.includes("Failed")) {
                clearInterval(intervalId);
                removeKey();
                setIsSaving(false);
                setShowErrorAndMessage({
                    message: result.data.ExceptionMessage ?? 'An error occurred while submitting  - Please Contact BEE123',
                    isError: true
                });
                setCounter(counter + 1);
                setProgress(0);
            } else if (result.data.Status.includes("CacheNotPopulated")) {
                if (progress === 100) {
                    setIsSaving(false);
                    setProgress(0);
                    props.closeSubmissionModal();
                }
                clearInterval(intervalId);
            }
        }
    }

    const handleDataSubmit = async (data: FieldValues) => {
        setIsSaving(true);
        setShowErrorAndMessage({
            message: '',
            isError: false
        });
        try {
            const submitDetails: Submissions.SubmitScorecardDto = {
                ScorecardId: props.scorecardId
            }

            await IntegrationProcessService.SubmitScorecard(submitDetails);
            signalRcheck();
        } catch (error) {
            console.log(error)
        }
    }

    const isFormValid = scorecardCheckbox && verificationAgencyCheckbox;

    return (
        <Dialog disableEnforceFocus open={props.showSubmissionModal} onClose={() => { props.closeSubmissionModal(false) }} aria-labelledby="form-dialog-title" maxWidth='lg'>
            <DialogTitle>
                <Typography variant="h4" sx={{ color: `${primary.main}` }}>
                    Confirm Scorecard Submission
                </Typography>
                <IconButton
                    aria-label="close"
                    onClick={() => props.closeSubmissionModal(true)}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ width: '750px', color: secondary.main }}>
                <form id='ScorecardSubmitForm'>
                    <Grid container>
                        <Typography variant='body2'>You're about to submit your scorecard for verification:</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={3} style={{ minWidth: "300px" }} paddingTop={2}>
                        <Card sx={{ width: "100%", borderRadius: '16px 0 16px 0', bgcolor: `${success.main}15`, boxShadow: '0' }}>
                            <CardContent style={{ padding: "10px" }}>
                                <Typography variant="body2" sx={{ color: `${success.main}` }}>
                                    Scorecard: <strong>{props.scorecardName}</strong>
                                </Typography>
                                <Typography variant="body2" sx={{ color: `${success.main}` }}>
                                    Verification Agency: <strong>{props.verificationAgencyName}</strong>
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid container paddingTop={2} direction="column">
                        <Grid item>
                            <Typography variant="h6" sx={{ color: `${primary.main}` }}>
                                <strong>Data Pages</strong>
                            </Typography>
                        </Grid>
                        <Grid item>
                            <Typography variant='body2'>
                                The data pages listed will be submitted for verification.
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid container paddingLeft={3} paddingBottom={2} paddingTop={0.5}>
                        {allowedElements.map((element) => (
                            <Grid item xs={6} key={element.id} paddingTop={1}>
                                <div style={{ position: 'relative', paddingLeft: '20px' }}>
                                    <div style={{
                                        position: 'absolute',
                                        left: 0,
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        width: '4px',
                                        height: '4px',
                                        borderRadius: '50%',
                                        backgroundColor: 'black'
                                    }} />
                                    <Typography variant='body2'>{element.value}</Typography>
                                </div>
                            </Grid>
                        ))}
                    </Grid>
                    <Grid container paddingTop={1} >
                        <Typography variant='h6' sx={{ color: `${primary.main}` }}>
                            <strong>Supporting documents</strong>
                        </Typography>
                        <Typography variant='body2'>
                            All supporting documents uploaded to your scorecard will be included in this submission and will be available to your Verification Agency.
                        </Typography>
                    </Grid>
                    <Grid container paddingTop={2} direction="column">
                        <Grid item>
                            <Typography variant='h6' sx={{ color: `${primary.main}` }}>
                                <strong>Confirm Submission</strong>
                            </Typography>
                        </Grid>
                        <Grid item>
                            <Typography variant='body2'>
                                I confirm that I want to submit all data specified above as follows:
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid container direction="column" paddingTop={2}>
                        <Grid item>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={scorecardCheckbox}
                                        onChange={(e) => setScorecardCheckbox(e.target.checked)}
                                        name="scorecardCheckbox"
                                        color="primary"
                                        sx={{
                                            '& .MuiSvgIcon-root': {
                                                border: `2px solid ${theme.palette.secondary.main}`,
                                            },
                                        }}
                                    />
                                }
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Typography variant="body2">
                                            Scorecard: <strong>{props.scorecardName}</strong>
                                        </Typography>
                                    </Box>
                                }
                            />
                        </Grid>
                        <Grid item>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={verificationAgencyCheckbox}
                                        onChange={(e) => setVerificationAgencyCheckbox(e.target.checked)}
                                        name="verificationAgencyCheckbox"
                                        color="primary"
                                        sx={{
                                            '& .MuiSvgIcon-root': {
                                                border: `2px solid ${theme.palette.secondary.main}`,
                                            },
                                        }}
                                    />
                                }
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Typography variant="body2">
                                            Verification Agency: <strong>{props.verificationAgencyName}</strong>
                                        </Typography>
                                    </Box>
                                }
                            />
                        </Grid>
                    </Grid>
                    <Grid container paddingLeft={2} paddingTop={2} paddingBottom='0px'>
                        {(showErrorAndMessage.isError && showErrorAndMessage.message) &&
                            <Alert severity="error" sx={{ p: 2 }}>{showErrorAndMessage.message}</Alert>}
                    </Grid>
                </form>
            </DialogContent>

            <DialogActions>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                    <LoadingButton
                        loading={isSaving}
                        variant='contained'
                        form='AssignForm'
                        type="submit"
                        color='primary'
                        disabled={!isFormValid || isSaving}
                        style={{ marginLeft: '8px' }}
                        onClick={() => {
                            handleSubmit(handleDataSubmit)()
                                .catch(() => {
                                    setShowErrorAndMessage({
                                        message: 'An error occurred while submitting - Please Contact BEE123',
                                        isError: true
                                    })
                                });
                        }}>
                        Submit Scorecard
                    </LoadingButton>
                </Box>
            </DialogActions>

            {isSaving &&
                <Grid paddingLeft={3} paddingBottom={2}>
                    <LinearProgressWithLabel value={progress} />
                </Grid>
            }

        </Dialog>
    )
}